// src/pages/Today.tsx
/**
 * Today 頁面元件
 *
 * 顯示用戶當天的日程安排、時間箱和任務管理。主要功能包括：
 * - 當前時間箱狀態顯示與管理
 * - 展示接下來的時間箱
 * - 快速任務列表與管理
 * - AI 輔助日程規劃功能
 * - 即時捕捉 (Capture) 筆記功能
 *
 * 組件會定期檢查當前時間箱狀態，並在頁面可見性變化時更新資料。
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { format } from 'date-fns';

// Hooks
import { useToast } from '../contexts/ToastContext';
import { useHeader } from '../hooks/useHeader';
import { useTodayCalendarData } from '../contexts/TodayCalendarContext';
import { useCaptureCreate } from '../hooks/useCaptureCreate';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useCurrentTime } from '../hooks/useCurrentTime';
import { useDebugMode } from '../hooks/useDebugMode';
import { useTimeBoxManager } from '../features/timebox/hooks/useTimeBoxManager';
import { useAISuggestions } from '../hooks/useAISuggestions';
import { useCaptureManager } from '../hooks/useCaptureManager';
import { useEventManager } from '../features/event/hooks/useEventManager';
import { useMorningKickoff } from '../hooks/useMorningKickoff';
import { useOnboarding, useTipAutoMarker } from '../features/onboarding';
import { useDayPhase } from '../hooks/useDayPhase';
import { useDayCloseSection } from '../hooks/useDayCloseSection';
import { useInsight } from '../hooks/useInsight';
import { useTasks } from '../hooks/useTasks';
import { useTodayClosedTaskCount } from '../hooks/useTodayClosedTaskCount';
import { getTopTasksBySmartSort } from '../features/task/utils/taskSort';

// 服務與 API
import { getCurrentUserId } from '../services/auth';

// 工具與事件
import { formatDateToString } from '../utils/dateUtils';
import { logError } from '../utils/logger';

// 組件
import { RefreshCw } from '../components/common/icons';
import TimeBoxDetail from '../features/timebox/components/TimeBoxDetail';
import EventDetail from '../features/event/components/EventDetail';
import { CurrentScheduleItemCard, NowCard, MomentumCard, UpNextCard } from '../features/schedule';
import QuickCaptureSection from '../components/today/QuickCaptureSection';
import CaptureModal from '../components/captures/CaptureModal';
import DayCloseSection from '../components/dayClose/DayCloseSection';
import AISuggestionCard from '../components/suggestions/AISuggestionCard';
import AllDayEventsSubHeader from '../components/today/AllDayEventsSubHeader';
import { MorningKickoffCard } from '../components/morningKickoff/MorningKickoffCard';
import { InsightCard } from '../components/insight/InsightCard';
import { TipTooltip } from '../features/onboarding';
import { LessonsPanel } from '../features/onboarding/components/LessonsPanel';
const Today: React.FC = () => {
  // ======================================================
  // 引用與常量
  // ======================================================

  const { showError, showSuccess } = useToast();
  const { isDebugMode } = useDebugMode();
  const { getActiveTipForPage, dismissAndShowNext } = useOnboarding();
  useTipAutoMarker();

  const userId = getCurrentUserId();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const activeTip = getActiveTipForPage('today');

  /**
   * 穩定的今日日期對象 - 避免每次重新渲染都創建新的 Date 對象
   */
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  /**
   * 自動更新的當前時間 - 每30秒更新一次
   */
  const currentTime = useCurrentTime(30000);

  /**
   * 使用共享的今日日曆數據
   */
  const {
    timeBoxes,
    events,
    allDayEvents,
    currentItem,
    nextItem,
    isInitialized: isCalendarInitialized,
    refresh: fetchTimeBoxes,
  } = useTodayCalendarData();

  /**
   * 使用輕量級的 Capture 創建 hook
   */
  const {
    handleCreateCapture,
    isSubmitting: isCaptureSubmitting
  } = useCaptureCreate(userId);

  const checkIntervalRef = useRef<number | null>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * 數據刷新回調
   */
  const onDataRefresh = useCallback(() => {
    fetchTimeBoxes();
  }, [fetchTimeBoxes]);

  // ======================================================
  // 自定義 Hooks
  // ======================================================

  /**
   * TimeBox 管理
   */
  const timeBoxManager = useTimeBoxManager({
    userId,
    onRefresh: onDataRefresh,
    defaultCategory: 'focus',
  });

  /**
   * AI 建議管理
   */
  const aiSuggestions = useAISuggestions({
    userId,
    onRefresh: () => fetchTimeBoxes(),
    onSuccess: showSuccess,
    onError: showError,
  });

  /**
   * Capture 管理
   */
  const captureManager = useCaptureManager({
    userId,
    isMobile,
    onCreateCapture: handleCreateCapture,
    onSuccess: showSuccess,
    onError: showError,
  });

  /**
   * Event 管理
   */
  const eventManager = useEventManager();

  /**
   * Morning Kickoff 管理
   */
  const morningKickoff = useMorningKickoff({
    userId,
    currentTime,
    isDebugMode,
  });

  /**
   * 一天所處的心理階段（morning / active / evening）
   */
  const dayPhase = useDayPhase();

  /**
   * DayClose 狀態提升至 Today，讓 DayCloseSection banner 與
   * Evening 空狀態 CTA 共用同一個 modal
   */
  const dayCloseSection = useDayCloseSection();

  /**
   * Insight 卡片（Ideal Week drift 偵測）
   */
  const insight = useInsight();

  /**
   * Open tasks（Morning 文案脈絡 + Active Day UpNextCard）
   * local-first：先從 IndexedDB 載入，背景同步 Firestore
   */
  const { visibleTasks: openTasks } = useTasks(userId);

  /**
   * Smart sort 前 3 筆 open tasks（Active Day UpNextCard 使用）
   */
  const topOpenTasks = useMemo(() => {
    return getTopTasksBySmartSort(openTasks, today, 3);
  }, [openTasks, today]);

  /**
   * Evening 摘要：今日已結束的 timebox 數量
   * TimeBox 沒有 completed 狀態，以「結束時間已過且未取消」為準
   */
  const completedTimeboxCount = useMemo(() => {
    if (!timeBoxes) return 0;
    const now = new Date();
    const todayStr = formatDateToString(now);
    return timeBoxes.filter(tb =>
      tb.status !== 'cancelled' &&
      formatDateToString(new Date(tb.startTime)) === todayStr &&
      new Date(tb.endTime) < now
    ).length;
  }, [timeBoxes]);

  /**
   * Evening 摘要：今日 closed task 數量
   * 直接從 IndexedDB 讀取，不需要 Firestore subscription
   */
  const closedTodayTaskCount = useTodayClosedTaskCount(userId);

  /**
   * 今天是否已完成任何工作（task 或 timebox）
   * 場景 F vs D/E 的分水嶺
   */
  const hasCompletedWork = closedTodayTaskCount > 0 || completedTimeboxCount > 0;

  // ======================================================
  // 計算屬性
  // ======================================================

  /**
   * 獲取第二個即將到來的項目
   */
  const secondNextItem = useMemo(() => {
    if (!timeBoxes && !events) return null;

    const now = new Date();
    const allItems = [...(timeBoxes || []), ...(events || [])]
      .filter(item => new Date(item.startTime) > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    return allItems.length > 1 ? allItems[1] : null;
  }, [timeBoxes, events]);

  // ======================================================
  // 事件處理函數
  // ======================================================

  /**
   * 手動刷新所有數據
   */
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(prevIsRefreshing => {
      if (prevIsRefreshing) return prevIsRefreshing;
      return true;
    });

    try {
      await Promise.all([
        fetchTimeBoxes(),
        captureManager.fetchRecentCaptures()
      ]);
    } catch (error) {
      logError('手動刷新失敗', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchTimeBoxes, captureManager.fetchRecentCaptures]);

  /**
   * 處理時間箱調整
   */
  const handleTimeBoxAdjust = useCallback((timeBoxId: string) => {
    timeBoxManager.handleTimeBoxAdjust(timeBoxId, timeBoxes);
  }, [timeBoxManager, timeBoxes]);

  /**
   * 處理事件調整
   */
  const handleEventAdjust = useCallback((eventId: string) => {
    eventManager.openEventDetail(eventId);
  }, [eventManager]);

  /**
   * 處理 AI 建議按鈕點擊
   */
  const handleGetAISuggestion = useCallback(() => {
    aiSuggestions.handleGetAISuggestion(nextItem);
  }, [aiSuggestions, nextItem]);

  // ======================================================
  // Header 配置
  // ======================================================

  const HeaderActions = useMemo(() => {
    return [
      <button
        key="refresh"
        onClick={handleManualRefresh}
        disabled={isRefreshing}
        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        title="刷新"
        aria-label="Refresh"
      >
        <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
      </button>
    ];
  }, [handleManualRefresh, isRefreshing]);

  const formattedDateTime = useMemo(() => {
    const weekdayFull = format(today, 'EEEE');
    const monthFull = format(today, 'MMMM');
    const day = today.getDate();
    const time = format(currentTime, 'h:mm a');

    return `${weekdayFull}, ${monthFull} ${day} • ${time}`;
  }, [today, currentTime]);

  const stableAllDayEvents = useMemo(() => {
    if (!allDayEvents || allDayEvents.length === 0) return [];
    return [...allDayEvents];
  }, [allDayEvents?.length, allDayEvents?.map(e => e.id).join(',')]);

  const headerConfig = useMemo(() => ({
    title: 'Today',
    subtitle: formattedDateTime,
    rightActions: HeaderActions,
    subHeader: <AllDayEventsSubHeader allDayEvents={stableAllDayEvents} />
  }), [HeaderActions, formattedDateTime, stableAllDayEvents]);

  useHeader(headerConfig);

  // ======================================================
  // 副作用 (Effects)
  // ======================================================

  /**
   * 初始數據加載
   */
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchTimeBoxes(),
          captureManager.fetchRecentCaptures()
        ]);
      } catch (error) {
        logError("初始數據加載失敗", error);
      }
    };

    loadInitialData();
  }, [fetchTimeBoxes, captureManager.fetchRecentCaptures]);

  /**
   * 設置定時檢查時間箱狀態
   */
  useEffect(() => {
    if (checkIntervalRef.current) return;

    checkIntervalRef.current = window.setInterval(() => {
      fetchTimeBoxes();
    }, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTimeBoxes();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchTimeBoxes]);




  // ======================================================
  // 渲染 UI
  // ======================================================

  return (
    <>
    <div className="h-full flex flex-col content-standard">
      <div className="pb-24 flex flex-col gap-6 flex-1">
        {/* Insight 卡片：出現時排在最上方，出現在 DayCloseSection 之前 */}
        {insight.insight && (
          <InsightCard
            insight={insight.insight}
            onAccept={insight.handleAccept}
            onSnooze={insight.handleSnooze}
            onDismiss={insight.handleDismiss}
          />
        )}

        <div>
          <DayCloseSection
            isEveningTime={dayCloseSection.isEveningTime}
            dayClose={dayCloseSection.dayClose}
            isLoading={dayCloseSection.isLoading}
            error={dayCloseSection.error}
            isModalOpen={dayCloseSection.isModalOpen}
            onOpen={dayCloseSection.handleOpen}
            onClose={dayCloseSection.handleClose}
            onRetry={dayCloseSection.handleRetry}
          />
        </div>

        {/* Morning Kickoff 卡片 */}
        {morningKickoff.kickoffType && (
          <MorningKickoffCard
            date={formatDateToString(new Date())}
            onConfirmed={morningKickoff.handleKickoffConfirmed}
            timeBoxes={timeBoxes}
            events={events}
          />
        )}

        {/* 當前排程項目（currentItem 存在時） */}
        {currentItem ? (
          <NowCard
            currentItem={currentItem}
            isLoading={!isCalendarInitialized}
            onAdjustTimeBox={handleTimeBoxAdjust}
            onAdjustEvent={handleEventAdjust}
          />
        ) : (
          /* 舊版 CurrentScheduleItemCard 保留 Evening 空狀態
             TODO(#234): Evening 整合後移除此分支，由 DayCloseSection 接管 */
          dayPhase === 'evening' && (
            <CurrentScheduleItemCard
              currentItem={null}
              nextItem={nextItem}
              isLoading={!isCalendarInitialized}
              onAdjustTimeBox={handleTimeBoxAdjust}
              onAdjustEvent={handleEventAdjust}
              onRefresh={onDataRefresh}
              dayPhase={dayPhase}
              completedTimeboxCount={completedTimeboxCount}
              closedTodayTaskCount={closedTodayTaskCount}
              onOpenDayClose={dayCloseSection.handleOpen}
            />
          )
        )}

        {/* AI 建議卡片 */}
        {!currentItem && aiSuggestions.showAISuggestion && (
          <AISuggestionCard
            suggestions={aiSuggestions.suggestions}
            overallReason={aiSuggestions.overallReason}
            isLoading={aiSuggestions.isLoadingSuggestions}
            error={aiSuggestions.suggestionError}
            onSelectSuggestion={aiSuggestions.handleAISuggestionSelect}
            onRefresh={() => aiSuggestions.loadSuggestions(nextItem)}
          />
        )}

        {/* 主工作區佈局（依場景決定結構） */}
        {(() => {
          // Evening：MomentumCard 不出現，DayCloseSection 接管
          if (dayPhase === 'evening') {
            const showUpNextEvening = isCalendarInitialized && nextItem !== null;
            return (
              <div className={`grid gap-6 ${showUpNextEvening ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {showUpNextEvening && (
                  <UpNextCard
                    nextItem={nextItem}
                    secondNextItem={secondNextItem}
                    isLoading={!isCalendarInitialized}
                    dayPhase={dayPhase}
                    topOpenTasks={topOpenTasks}
                  />
                )}
                <div className="relative">
                  <QuickCaptureSection
                    recentCaptures={captureManager.recentCaptures}
                    captureInput={captureManager.captureInput}
                    onCaptureInputChange={captureManager.setCaptureInput}
                    onSubmitCapture={captureManager.handleQuickCapture}
                    isSubmitting={isCaptureSubmitting}
                    onExpand={captureManager.handleExpandCapture}
                    isFullWidth={!showUpNextEvening}
                  />
                  {activeTip === 'today-quick-capture' && (
                    <TipTooltip
                      tipId="today-quick-capture"
                      onClose={() => dismissAndShowNext('today-quick-capture', 'today')}
                    />
                  )}
                </div>
              </div>
            );
          }

          // currentItem 存在（場景 A）：NowCard 已渲染，下方 grid = NextUpCard + QuickCapture
          if (currentItem) {
            const showUpNext = isCalendarInitialized && nextItem !== null;
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {showUpNext && (
                  <UpNextCard
                    nextItem={nextItem}
                    secondNextItem={secondNextItem}
                    isLoading={!isCalendarInitialized}
                    dayPhase={dayPhase}
                    topOpenTasks={topOpenTasks}
                  />
                )}
                <div className={`relative ${!showUpNext ? 'md:col-span-2' : ''}`}>
                  <QuickCaptureSection
                    recentCaptures={captureManager.recentCaptures}
                    captureInput={captureManager.captureInput}
                    onCaptureInputChange={captureManager.setCaptureInput}
                    onSubmitCapture={captureManager.handleQuickCapture}
                    isSubmitting={isCaptureSubmitting}
                    onExpand={captureManager.handleExpandCapture}
                    isFullWidth={!showUpNext}
                  />
                  {activeTip === 'today-quick-capture' && (
                    <TipTooltip
                      tipId="today-quick-capture"
                      onClose={() => dismissAndShowNext('today-quick-capture', 'today')}
                    />
                  )}
                </div>
              </div>
            );
          }

          // currentItem 不存在，Morning / Active（場景 B-F）
          if (nextItem) {
            // 場景 B/C：NextUpCard 全寬上方，MomentumCard + QuickCapture grid
            return (
              <>
                <UpNextCard
                  nextItem={nextItem}
                  secondNextItem={secondNextItem}
                  isLoading={!isCalendarInitialized}
                  dayPhase={dayPhase}
                  topOpenTasks={topOpenTasks}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <MomentumCard
                    nextItem={nextItem}
                    hasCompletedWork={hasCompletedWork}
                    dayPhase={dayPhase}
                    topOpenTasks={topOpenTasks}
                    isLoading={!isCalendarInitialized}
                    onRefresh={onDataRefresh}
                    onGetAISuggestion={handleGetAISuggestion}
                  />
                  <div className="relative">
                    <QuickCaptureSection
                      recentCaptures={captureManager.recentCaptures}
                      captureInput={captureManager.captureInput}
                      onCaptureInputChange={captureManager.setCaptureInput}
                      onSubmitCapture={captureManager.handleQuickCapture}
                      isSubmitting={isCaptureSubmitting}
                      onExpand={captureManager.handleExpandCapture}
                      isFullWidth={false}
                    />
                    {activeTip === 'today-quick-capture' && (
                      <TipTooltip
                        tipId="today-quick-capture"
                        onClose={() => dismissAndShowNext('today-quick-capture', 'today')}
                      />
                    )}
                  </div>
                </div>
              </>
            );
          }

          // 場景 D/E/F：MomentumCard 全寬，QuickCapture 全寬
          return (
            <>
              <MomentumCard
                nextItem={null}
                hasCompletedWork={hasCompletedWork}
                dayPhase={dayPhase}
                topOpenTasks={topOpenTasks}
                isLoading={!isCalendarInitialized}
                onRefresh={onDataRefresh}
                onGetAISuggestion={handleGetAISuggestion}
              />
              <div className="relative">
                <QuickCaptureSection
                  recentCaptures={captureManager.recentCaptures}
                  captureInput={captureManager.captureInput}
                  onCaptureInputChange={captureManager.setCaptureInput}
                  onSubmitCapture={captureManager.handleQuickCapture}
                  isSubmitting={isCaptureSubmitting}
                  onExpand={captureManager.handleExpandCapture}
                  isFullWidth={true}
                />
                {activeTip === 'today-quick-capture' && (
                  <TipTooltip
                    tipId="today-quick-capture"
                    onClose={() => dismissAndShowNext('today-quick-capture', 'today')}
                  />
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* TimeBox 詳情（新：使用 inline edit）*/}
      <TimeBoxDetail
        isOpen={timeBoxManager.showTimeBoxModal}
        timeBoxId={timeBoxManager.selectedTimeBoxId}
        onClose={timeBoxManager.closeTimeBoxModal}
        onRefresh={onDataRefresh}
        mode={timeBoxManager.modalMode}
      />

      {/* Event 詳情（使用 inline edit）*/}
      <EventDetail
        isOpen={eventManager.showEventDetail}
        eventId={eventManager.selectedEventId}
        onClose={eventManager.closeEventDetail}
        onRefresh={onDataRefresh}
      />

      {/* Capture 模態框 */}
      {captureManager.showCaptureModal && (
        <CaptureModal
          isOpen={captureManager.showCaptureModal}
          onClose={captureManager.closeCaptureModal}
          onSubmit={captureManager.handleCaptureModalSubmit}
          initialContent={captureManager.modalInitialContent}
          userId={userId}
          isSubmitting={isCaptureSubmitting}
        />
      )}

      {/* 底部導航預留空間 */}
      <div className="h-16"></div>

    </div>

    {/* Onboarding Lessons 清單（Portal，fixed 右下角） */}
    <LessonsPanel />

    </>
  );
};

export default Today;
